import { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Completion Glow Micro-Interaction
 * 
 * A subtle, calm golden glow animation that plays when a user completes an action.
 * Designed to feel premium and rewarding while maintaining the calm Reclaim aesthetic.
 * 
 * Usage:
 * const { triggerGlow, GlowOverlay } = useCompletionGlow();
 * 
 * // In your component:
 * <div className="relative">
 *   <GlowOverlay />
 *   <button onClick={() => { doAction(); triggerGlow(); }}>Complete</button>
 * </div>
 */

export const useCompletionGlow = () => {
  const [isGlowing, setIsGlowing] = useState(false);

  const triggerGlow = useCallback(() => {
    setIsGlowing(true);
    // Auto-reset after animation
    setTimeout(() => setIsGlowing(false), 1200);
  }, []);

  const GlowOverlay = ({ className = "" }) => (
    <AnimatePresence>
      {isGlowing && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className={`absolute inset-0 pointer-events-none z-10 ${className}`}
          data-testid="completion-glow-overlay"
        >
          {/* Inner glow - warm golden */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ 
              opacity: [0, 0.6, 0],
              scale: [0.8, 1.05, 1.1]
            }}
            transition={{ 
              duration: 1.2,
              ease: [0.16, 1, 0.3, 1],
              times: [0, 0.4, 1]
            }}
            className="absolute inset-0 rounded-inherit"
            style={{
              background: 'radial-gradient(circle at center, rgba(184, 149, 108, 0.25) 0%, rgba(184, 149, 108, 0.08) 50%, transparent 70%)',
              borderRadius: 'inherit'
            }}
          />
          
          {/* Outer glow ring */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ 
              opacity: [0, 0.4, 0],
              scale: [0.9, 1.1, 1.15]
            }}
            transition={{ 
              duration: 1,
              ease: "easeOut",
              times: [0, 0.3, 1]
            }}
            className="absolute inset-0"
            style={{
              boxShadow: '0 0 40px rgba(184, 149, 108, 0.3), 0 0 80px rgba(184, 149, 108, 0.15)',
              borderRadius: 'inherit'
            }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );

  return { triggerGlow, isGlowing, GlowOverlay };
};

/**
 * CompletionGlowWrapper
 * 
 * Wrapper component that adds completion glow capability to any child element.
 * 
 * Usage:
 * <CompletionGlowWrapper trigger={isCompleted}>
 *   <Card>...</Card>
 * </CompletionGlowWrapper>
 */
export const CompletionGlowWrapper = ({ children, trigger, className = "" }) => {
  const { triggerGlow, GlowOverlay } = useCompletionGlow();

  // Trigger glow when trigger changes to true
  useState(() => {
    if (trigger) {
      triggerGlow();
    }
  }, [trigger, triggerGlow]);

  return (
    <div className={`relative ${className}`}>
      <GlowOverlay />
      {children}
    </div>
  );
};

/**
 * Standalone glow animation component
 * Can be rendered anywhere to show a full-screen subtle glow
 */
export const FullScreenGlow = ({ show, onComplete }) => {
  return (
    <AnimatePresence onExitComplete={onComplete}>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="fixed inset-0 pointer-events-none z-50"
          data-testid="fullscreen-glow"
        >
          {/* Warm golden vignette from center */}
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ 
              opacity: [0, 0.4, 0],
              scale: [0.5, 1.5, 2]
            }}
            transition={{ 
              duration: 1.5,
              ease: [0.16, 1, 0.3, 1]
            }}
            className="absolute inset-0 flex items-center justify-center"
          >
            <div 
              className="w-full h-full"
              style={{
                background: 'radial-gradient(circle at center, rgba(184, 149, 108, 0.15) 0%, rgba(184, 149, 108, 0.05) 30%, transparent 60%)'
              }}
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default useCompletionGlow;
